// Keep this boundary dependency-free: it is also compiled into standalone Node API functions.
// greekLocale.test.ts verifies parity with the app/shared contract.
export const SUPPORTED_LOCALES = ["en", "de", "es", "ru", "tr", "el"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const PREFIXED_LOCALES = new Set<SupportedLocale>(["de", "es", "ru", "tr", "el"]);

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function normalizeLocale(language: string | undefined): SupportedLocale {
  const baseLanguage = language?.toLowerCase().split("-")[0];
  return isSupportedLocale(baseLanguage) ? baseLanguage : "en";
}

export function getLocaleFromPathname(pathname: string): SupportedLocale {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  return isSupportedLocale(firstSegment) ? firstSegment : "en";
}

export function stripLocalePrefix(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0 || !isSupportedLocale(segments[0])) return pathname || "/";

  const rest = segments.slice(1).join("/");
  return rest ? `/${rest}` : "/";
}

export function getLocalePrefix(language: string | undefined): string {
  const locale = normalizeLocale(language);
  return PREFIXED_LOCALES.has(locale) ? `/${locale}` : "";
}

export function localizePath(path: string, language: string | undefined): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const pathWithoutLocale = stripLocalePrefix(normalizedPath);
  const prefix = getLocalePrefix(language);

  if (pathWithoutLocale === "/") return prefix || "/";
  return `${prefix}${pathWithoutLocale}`;
}
